import { useState, useContext, useMemo } from 'react';
import axios from 'src/axios';
import { AppContext } from 'src/store/ContextStore';
import { EVENTS } from 'src/components/admin/appReducer';

function dedupeByName(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const key = (item?.name || '').toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function useClinicalSuggestions({
  familyMemberId,
  isProband,
  onAccepted
}) {
  const { content, dispatchUpdate } = useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;
  const user_id = user?.user_id || user?._id;
  const [attachmentSuggestions, setAttachmentSuggestions] = useState([]);
  const [noteSuggestions, setNoteSuggestions] = useState([]);
  const [analyzingNote, setAnalyzingNote] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [savingNote, setSavingNote] = useState(false);

  const suggestions = useMemo(
    () => dedupeByName([...attachmentSuggestions, ...noteSuggestions]),
    [attachmentSuggestions, noteSuggestions]
  );

  const analyzeText = async (text = '', source = 'blur') => {
    const trimmed = (text || '').trim();
    const bar = '─'.repeat(60);

    if (!trimmed) {
      if (source !== 'pdf-upload') {
        setNoteSuggestions([]);
        setSelectedSuggestions((prev) =>
          prev.filter((s) => attachmentSuggestions.some((a) => a.id === s.id))
        );
      }
      return;
    }

    console.log(
      `\n${bar}\n[SUGGESTION FLOW] STEP 3: ANALYSIS TRIGGERED (frontend)\n` +
        `  Source  : ${source}\n` +
        `  Text len: ${trimmed.length} chars\n` +
        `  Preview : "${trimmed.slice(0, 120)}${
          trimmed.length > 120 ? '…' : ''
        }"\n${bar}`
    );

    setAnalyzingNote(true);
    try {
      const res = await axios.post('/clinical-notes/analyze-note', {
        noteText: trimmed,
        userId: user_id,
        familyMemberId,
        isProband: !!isProband
      });
      if (res.data.success) {
        const incoming = dedupeByName([
          ...(res.data.phenotypes || []).map((i) => ({ ...i, type: 'hpo' })),
          ...(res.data.diseases || []).map((i) => ({
            ...i,
            type: 'icd-10-cm'
          })),
          ...(res.data.genotypes || []).map((i) => ({ ...i, type: 'hgnc' }))
        ]);
        console.log(
          `\n${bar}\n[SUGGESTION FLOW] STEP 6: SUGGESTIONS RECEIVED (frontend)\n` +
            `  Source     : ${source}\n` +
            `  Phenotypes : ${(res.data.phenotypes || []).length}\n` +
            `  Diseases   : ${(res.data.diseases || []).length}\n` +
            `  Genotypes  : ${(res.data.genotypes || []).length}\n` +
            `  Incoming (deduped): ${incoming.length}\n` +
            `  Items: ${
              incoming.map((i) => `"${i.name}" [${i.id}]`).join(', ') ||
              '(none)'
            }\n${bar}`
        );
        if (source === 'pdf-upload') {
          setAttachmentSuggestions((prev) =>
            dedupeByName([...prev, ...incoming])
          );
        } else {
          setNoteSuggestions(incoming);
          setSelectedSuggestions((prev) =>
            prev.filter(
              (s) =>
                attachmentSuggestions.some((a) => a.id === s.id) ||
                incoming.some((i) => i.id === s.id)
            )
          );
        }
      }
    } catch (err) {
      console.warn('Analyze-note request failed:', err?.message || err);
    } finally {
      setAnalyzingNote(false);
    }
  };

  const handleTextBlur = (e) =>
    analyzeText(e?.target?.value || '', 'text-blur');
  const handleAttachmentText = (text) => analyzeText(text, 'pdf-upload');

  const toggleSuggestion = (item) => {
    setSelectedSuggestions((prev) => {
      const exists = prev.some((s) => s.id === item.id);
      return exists ? prev.filter((s) => s.id !== item.id) : [...prev, item];
    });
  };

  const acceptSuggestions = async ({ clinicalNoteDate, clinician } = {}) => {
    if (!selectedSuggestions.length) return { success: false };
    setSavingNote(true);
    try {
      const phenotypes = selectedSuggestions.filter((i) =>
        i.id?.startsWith('HP:')
      );
      const genotypes = selectedSuggestions.filter((i) =>
        i.id?.startsWith('NCBIGene:')
      );
      const diseases = selectedSuggestions.filter(
        (i) => !i.id?.startsWith('HP:') && !i.id?.startsWith('NCBIGene:')
      );
      const res = await axios.post('/clinical-notes/accept-suggestions', {
        userId: user_id,
        familyMemberId,
        familyId: user.familyId,
        isProband: !!isProband,
        clinicalNoteDate,
        clinician,
        phenotypes,
        genotypes,
        diseases
      });
      if (res.data.success) {
        // Strip stale aggregates so the reducer recomputes them from updated member data
        /* eslint-disable no-unused-vars */
        const {
          diagnoses: _d,
          phenotypes: _p,
          genotypes: _g,
          ...usersBase
        } = users;
        /* eslint-enable no-unused-vars */
        const memberUpdate = {
          phenotypes: res.data.phenotypes || [],
          genotypes: res.data.genotypes || [],
          diagnoses: res.data.diagnoses || []
        };
        dispatchUpdate({
          type: EVENTS.SET_PEDIGREE_SELECTED_USER,
          value: isProband
            ? {
                ...usersBase,
                selectedUser: { ...users.selectedUser, ...memberUpdate }
              }
            : {
                ...usersBase,
                family: (users.family || []).map((m) =>
                  m.familyMemberId === familyMemberId
                    ? { ...m, ...memberUpdate }
                    : m
                )
              }
        });
        onAccepted?.({
          phenotypes: res.data.phenotypes,
          genotypes: res.data.genotypes,
          diagnoses: res.data.diagnoses
        });
        setAttachmentSuggestions([]);
        setNoteSuggestions([]);
        setSelectedSuggestions([]);
        return { success: true, count: res.data.count };
      }
    } catch {
      // caller shows the error
    } finally {
      setSavingNote(false);
    }
    return { success: false };
  };

  return {
    suggestions,
    analyzingNote,
    selectedSuggestions,
    savingNote,
    handleTextBlur,
    handleAttachmentText,
    toggleSuggestion,
    acceptSuggestions,
    analyzeText
  };
}
